/**
 * KitsPage.js - Gestão de Kits/Bundles
 * Artigos Compostos com múltiplos componentes
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ModulePageHero } from '@/components/common/ModulePageHero';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';
import { 
  Plus, Search, Edit, Trash2, Eye,
  Layers, Check, ChevronsUpDown, RotateCcw, SlidersHorizontal
} from 'lucide-react';
import { formatCurrency, cn } from '@/lib/utils';
import { productsAPI, categoriesAPI, catalogsAPI } from '@/lib/api';
import { safeRequest } from '@/lib/safeRequest';
import { getStoredAuthToken } from '@/lib/authStorage';
import { useAuth } from '@/hooks/useAuth';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_BASE_URL });
api.interceptors.request.use(config => {
  const token = getStoredAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const kitsAPI = {
  list: (params) => api.get('/api/kits/', { params }),
  get: (id) => api.get(`/api/kits/${id}`),
  create: (data) => api.post('/api/kits/', data),
  approve: (id) => api.post(`/api/kits/${id}/approve`),
  update: (id, data) => api.put(`/api/kits/${id}`, data),
  delete: (id, reason) => api.delete(`/api/kits/${id}`, { params: { reason } }),
  checkAvailability: (id) => api.get(`/api/kits/${id}/availability`)
};

const statusBadgeMap = {
  draft: 'bg-amber-100 text-amber-800 border-amber-200',
  approved: 'bg-blue-100 text-blue-800 border-blue-200',
  active: 'bg-emerald-100 text-emerald-800 border-emerald-200'
};

const pricingMethods = [
  { value: 'sum', label: 'Soma dos componentes' },
  { value: 'fixed', label: 'Preço fixo' },
  { value: 'discount', label: 'Desconto sobre soma' }
];

const componentTypes = [
  { value: 'required', label: 'Obrigatório' },
  { value: 'optional', label: 'Opcional' },
  { value: 'alternative', label: 'Alternativo' }
];

const getKitCompositionSummary = (kit) => {
  const names = Array.isArray(kit?.components)
    ? kit.components
        .map((component) => component.product_name || component.name || component.product_sku || component.sku)
        .filter(Boolean)
    : [];

  if (!names.length) {
    return 'Composição por detalhar';
  }

  const preview = names.slice(0, 3).join(' · ');
  return names.length > 3 ? `${preview} +${names.length - 3}` : preview;
};

// Searchable Product Combobox Component
function ProductCombobox({ products, value, onValueChange, placeholder = "Pesquisar produto...", testId = 'kit-product-combobox' }) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const selectedProduct = products.find(p => p.id === value);
  
  // Filter products based on search query
  const filteredProducts = searchQuery.length > 0
    ? products.filter(p => 
        p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.brand?.toLowerCase().includes(searchQuery.toLowerCase())
      ).slice(0, 50) // Limit to 50 results for performance
    : products.slice(0, 50);
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid={`${testId}-trigger`}
        >
          {selectedProduct ? (
            <span className="truncate">
              {selectedProduct.name} ({formatCurrency(selectedProduct.unit_price)})
            </span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder="Escreva para pesquisar..." 
            value={searchQuery}
            onValueChange={setSearchQuery}
            data-testid={`${testId}-search-input`}
          />
          <CommandList>
            <CommandEmpty>Nenhum produto encontrado.</CommandEmpty>
            <CommandGroup>
              {filteredProducts.map((product) => (
                <CommandItem
                  key={product.id}
                  value={product.id}
                  data-testid={`${testId}-option-${product.id}`}
                  onSelect={() => {
                    onValueChange(product.id);
                    setOpen(false);
                    setSearchQuery('');
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === product.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex flex-col">
                    <span className="font-medium">{product.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {product.sku} • {formatCurrency(product.unit_price)}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function KitsPage() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole(['admin']);
  const [kits, setKits] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);  // Legacy categories
  const [productCategories, setProductCategories] = useState([]);  // New catalog categories
  const [loading, setLoading] = useState(true);
  const [catalogLookupLoaded, setCatalogLookupLoaded] = useState(false);
  const [catalogLookupLoading, setCatalogLookupLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [kitToDelete, setKitToDelete] = useState(null);
  const [selectedKit, setSelectedKit] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [approvingKitId, setApprovingKitId] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    description: '',
    category: '',
    components: [],
    pricing_method: 'sum',
    fixed_price: '',
    discount_percent: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadKits = useCallback(async () => {
    try {
      const response = await kitsAPI.list({ limit: 100 });
      setKits(response.data.items || []);
    } catch (error) {
      console.error('Failed to load kits:', error);
      toast.error('Erro ao carregar kits');
    }
  }, []);

  const loadCatalogLookups = useCallback(async () => {
    if (catalogLookupLoaded || catalogLookupLoading) return;

    try {
      setCatalogLookupLoading(true);
      const [productsRes, categoriesRes] = await Promise.all([
        safeRequest(() => productsAPI.list({ limit: 500, compact: true }), { data: [] }),
        safeRequest(() => categoriesAPI.list(), { data: [] }),
      ]);
      const productsData = productsRes.data;
      setProducts(Array.isArray(productsData) ? productsData : (productsData.items || []));
      setCategories(categoriesRes.data || []);
      setCatalogLookupLoaded(true);
    } catch (error) {
      console.error('Failed to load kit catalog lookups:', error);
    } finally {
      setCatalogLookupLoading(false);
    }
  }, [catalogLookupLoaded, catalogLookupLoading]);

  const loadData = async () => {
    const failedRequests = [];
    try {
      setLoading(true);
      const [kitsRes, productCategoriesRes] = await Promise.all([
        safeRequest(() => kitsAPI.list({ limit: 100 }), { data: { items: [] } }, { onError: () => failedRequests.push('kits') }),
        safeRequest(() => catalogsAPI.listProductCategories(), { data: [] }, { onError: () => failedRequests.push('product-categories') })
      ]);
      setKits(kitsRes.data.items || []);
      setProductCategories(Array.isArray(productCategoriesRes) ? productCategoriesRes : productCategoriesRes.data || []);
      if (failedRequests.length) {
        toast.info('Alguns dados do módulo de kits vieram vazios ou indisponíveis, mas a página foi desbloqueada.');
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
      void loadCatalogLookups();
    }
  };

  useEffect(() => {
    if (dialogOpen) {
      loadCatalogLookups();
    }
  }, [dialogOpen, loadCatalogLookups]);

  const filteredKits = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return kits.filter((kit) => {
      const matchesStatus = statusFilter === 'all' ? true : (kit.status || 'draft') === statusFilter;
      if (!matchesStatus) return false;
      if (!query) return true;

      return [
        kit.name,
        kit.kit_code,
        kit.sku,
        kit.category,
        kit.created_by_name,
        kit.approved_by_name,
      ].some((value) => String(value || '').toLowerCase().includes(query));
    });
  }, [kits, searchTerm, statusFilter]);

  const availabilitySummary = useMemo(() => (
    kits.reduce((sum, kit) => sum + Number(kit.available_quantity || 0), 0)
  ), [kits]);

  const averageMargin = useMemo(() => {
    if (!kits.length) return 0;
    const total = kits.reduce((sum, kit) => sum + Number(kit.margin_percent || 0), 0);
    return total / kits.length;
  }, [kits]);

  const showEmptyState = !loading && filteredKits.length === 0;

  const statusOverview = useMemo(() => ([
    {
      value: 'all',
      label: 'Todos',
      count: kits.length,
    },
    {
      value: 'draft',
      label: 'Rascunhos',
      count: kits.filter((kit) => (kit.status || 'draft') === 'draft').length,
    },
    {
      value: 'approved',
      label: 'Aprovados',
      count: kits.filter((kit) => kit.status === 'approved').length,
    },
    {
      value: 'active',
      label: 'Activos',
      count: kits.filter((kit) => kit.status === 'active').length,
    },
  ]), [kits]);

  const handleAddComponent = () => {
    setFormData(prev => ({
      ...prev,
      components: [...prev.components, {
        product_id: '',
        quantity: 1,
        component_type: 'required',
        use_product_price: true,
        custom_price: ''
      }]
    }));
  };

  const handleRemoveComponent = (index) => {
    setFormData(prev => ({
      ...prev,
      components: prev.components.filter((_, i) => i !== index)
    }));
  };

  const handleComponentChange = (index, field, value) => {
    setFormData(prev => {
      const newComponents = [...prev.components];
      newComponents[index] = { ...newComponents[index], [field]: value };
      return { ...prev, components: newComponents };
    });
  };

  const calculateTotals = () => {
    let totalCost = 0;
    let totalPrice = 0;

    formData.components.forEach(comp => {
      const product = products.find(p => p.id === comp.product_id);
      if (product) {
        const qty = comp.quantity || 1;
        totalCost += (product.cost_price || 0) * qty;
        if (comp.use_product_price) {
          totalPrice += (product.unit_price || 0) * qty;
        } else {
          totalPrice += parseFloat(comp.custom_price) || 0;
        }
      }
    });

    let finalPrice = totalPrice;
    if (formData.pricing_method === 'fixed' && formData.fixed_price) {
      finalPrice = parseFloat(formData.fixed_price);
    } else if (formData.pricing_method === 'discount' && formData.discount_percent) {
      finalPrice = totalPrice * (1 - parseFloat(formData.discount_percent) / 100);
    }

    const margin = finalPrice - totalCost;
    const marginPercent = finalPrice > 0 ? (margin / finalPrice) * 100 : 0;

    return { totalCost, totalPrice, finalPrice, margin, marginPercent };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.components.length === 0) {
      toast.error('Adicione pelo menos um componente');
      return;
    }

    try {
      const submitData = {
        ...formData,
        fixed_price: formData.fixed_price ? parseFloat(formData.fixed_price) : null,
        discount_percent: formData.discount_percent ? parseFloat(formData.discount_percent) : null,
        components: formData.components.map(c => ({
          ...c,
          quantity: parseInt(c.quantity) || 1,
          custom_price: c.custom_price ? parseFloat(c.custom_price) : null
        }))
      };

      if (editMode && selectedKit) {
        submitData.justification = 'Actualização de kit';
        await kitsAPI.update(selectedKit.id, submitData);
        toast.success('Kit actualizado com sucesso');
      } else {
        const res = await kitsAPI.create(submitData);
        toast.success('Kit criado com sucesso', {
          description: `Código: ${res.data?.kit_code || 'N/A'}`
        });
      }

      setDialogOpen(false);
      resetForm();
      loadKits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao guardar kit');
    }
  };

  const handleEdit = (kit) => {
    setSelectedKit(kit);
    setFormData({
      name: kit.name,
      sku: kit.sku || '',
      description: kit.description || '',
      category: kit.category,
      components: kit.components || [],
      pricing_method: kit.pricing_method || 'sum',
      fixed_price: kit.fixed_price?.toString() || '',
      discount_percent: kit.discount_percent?.toString() || ''
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = async (kit) => {
    if (isAdmin) {
      try {
        await kitsAPI.delete(kit.id, 'Eliminação directa pelo administrador');
        toast.success('Kit eliminado pelo administrador');
        loadKits();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erro ao eliminar kit');
      }
      return;
    }

    setKitToDelete(kit);
    setDeleteDialogOpen(true);
  };

  const handleApproveKit = async (kit) => {
    setApprovingKitId(kit.id);
    try {
      const response = await kitsAPI.approve(kit.id);
      const approvedAt = response.data?.approved_at;
      toast.success('Kit aprovado para venda no POS', {
        description: approvedAt ? `Aprovado em ${approvedAt}` : undefined
      });
      loadKits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aprovar kit');
    } finally {
      setApprovingKitId(null);
    }
  };

  const confirmDelete = async () => {
    if (!kitToDelete) return;
    
    try {
      await kitsAPI.delete(kitToDelete.id, 'Eliminação manual');
      toast.success('Kit eliminado com sucesso');
      setDeleteDialogOpen(false);
      setKitToDelete(null);
      loadKits();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao eliminar kit');
    }
  };

  const handleViewDetail = async (kit) => {
    try {
      const [kitRes, availRes] = await Promise.all([
        kitsAPI.get(kit.id),
        kitsAPI.checkAvailability(kit.id)
      ]);
      setSelectedKit({ ...kitRes.data, availability: availRes.data });
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      sku: '',
      description: '',
      category: '',
      components: [],
      pricing_method: 'sum',
      fixed_price: '',
      discount_percent: ''
    });
    setSelectedKit(null);
    setEditMode(false);
  };

  const totals = calculateTotals();

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-5" data-testid="kits-page">
      <ModulePageHero
        testId="kits-page-hero"
        title="Kits e Bundles"
        description=""
        icon={Layers}
        badge="Bundles"
        compact
        stats={[
          { label: 'Total de Kits', value: kits.length },
          { label: 'Activos', value: kits.filter((kit) => kit.status === 'active').length },
          { label: 'Rascunhos', value: kits.filter((kit) => (kit.status || 'draft') === 'draft').length },
          { label: 'Componentes', value: kits.reduce((sum, kit) => sum + (kit.component_count || 0), 0) },
        ]}
        actions={(
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="h-10 rounded-xl px-4 text-sm" data-testid="new-kit-btn">
            <Plus className="h-4 w-4 mr-2" />
            Novo Kit
          </Button>
        )}
      />

      {/* Search */}
      <div className="space-y-3 rounded-[22px] border border-slate-200 bg-white p-3.5 shadow-sm" data-testid="kits-filters-panel">
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_220px_auto] lg:items-center">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Pesquisar por nome, código, categoria ou utilizador..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-10 rounded-xl pl-10 text-sm"
              data-testid="kits-search-input"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-10 rounded-xl text-sm" data-testid="kits-status-filter-select">
              <div className="flex items-center gap-2 text-sm">
                <SlidersHorizontal className="h-4 w-4 text-slate-500" />
                <SelectValue placeholder="Filtrar por estado" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os estados</SelectItem>
              <SelectItem value="draft">Rascunho</SelectItem>
              <SelectItem value="approved">Aprovado</SelectItem>
              <SelectItem value="active">Activo</SelectItem>
            </SelectContent>
          </Select>

          <Button type="button" variant="outline" size="sm" className="h-10 rounded-xl px-4 text-sm" onClick={resetFilters} data-testid="kits-reset-filters-button">
            <RotateCcw className="mr-2 h-4 w-4" />
            Limpar filtros
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2" data-testid="kits-status-overview-row">
          {statusOverview.map((status) => {
            const isActive = statusFilter === status.value;
            return (
              <Button
                key={status.value}
                type="button"
                variant="outline"
                onClick={() => setStatusFilter(status.value)}
                className={cn(
                  'rounded-full border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100',
                  isActive && 'border-slate-900 bg-slate-900 text-white hover:bg-slate-800'
                )}
                data-testid={`kits-status-chip-${status.value}`}
              >
                {status.label}
                <span className={cn('ml-2 rounded-full px-2 py-0.5 text-xs', isActive ? 'bg-white/20 text-white' : 'bg-white text-slate-700')}>
                  {status.count}
                </span>
              </Button>
            );
          })}
        </div>

        <div className="grid gap-3 md:grid-cols-3" data-testid="kits-insights-grid">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-xs uppercase tracking-wide text-slate-500">Visíveis agora</p>
            <p className="mt-1 text-xl font-semibold text-slate-900" data-testid="kits-visible-count">{filteredKits.length}</p>
            <p className="text-xs text-slate-500">de {kits.length} kits carregados</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-xs uppercase tracking-wide text-slate-500">Margem média</p>
            <p className="mt-1 text-xl font-semibold text-slate-900" data-testid="kits-average-margin">{averageMargin.toFixed(1)}%</p>
            <p className="text-xs text-slate-500">Leitura rápida do catálogo composto</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-xs uppercase tracking-wide text-slate-500">Disponibilidade total</p>
            <p className="mt-1 text-xl font-semibold text-slate-900" data-testid="kits-availability-summary">{availabilitySummary}</p>
            <p className="text-xs text-slate-500">Unidades agregadas prontas para venda</p>
          </div>
        </div>
      </div>

      {showEmptyState && (
        <EmptyStatePanel
          title="Sem dados disponíveis"
          description="Nenhum registo encontrado para Kits / Bundles nesta base. Pode configurar ou importar dados para começar."
          testId="kits-empty-state"
        />
      )}

      {/* Kits Grid */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4" data-testid="kits-grid">
        {filteredKits.length === 0 ? (
          <Card className="col-span-full border-dashed border-slate-300">
            <CardContent className="flex flex-col items-center justify-center py-10 text-center text-muted-foreground">
              <Layers className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-base font-medium text-slate-700" data-testid="kits-empty-state-title">Nenhum kit encontrado</p>
              <p className="mt-1 text-sm" data-testid="kits-empty-state-description">
                {kits.length === 0 ? 'Crie o primeiro kit para começar.' : 'Ajuste os filtros para voltar a mostrar resultados.'}
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {kits.length === 0 ? (
                  <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="kits-empty-state-create-button">
                    <Plus className="mr-2 h-4 w-4" /> Criar kit
                  </Button>
                ) : (
                  <Button variant="outline" onClick={resetFilters} data-testid="kits-empty-state-reset-button">
                    <RotateCcw className="mr-2 h-4 w-4" /> Limpar filtros
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          filteredKits.map(kit => (
            <Card key={kit.id} className="border-slate-200 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md" data-testid={`kit-card-${kit.id}`}>
              <CardHeader className="px-4 py-3.5">
                <div className="flex justify-between items-start">
                  <div className="min-w-0">
                    <CardTitle className="truncate text-base font-semibold" data-testid={`kit-name-${kit.id}`}>{kit.name}</CardTitle>
                    <p className="truncate text-xs text-muted-foreground" data-testid={`kit-code-${kit.id}`}>
                      {kit.kit_code || kit.sku}
                    </p>
                    <p className="truncate text-xs text-muted-foreground" data-testid={`kit-created-by-${kit.id}`}>
                      Criado por: {kit.created_by_name || 'N/A'}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge variant="outline" data-testid={`kit-component-count-${kit.id}`}>{kit.component_count} itens</Badge>
                    <Badge className={cn('border', statusBadgeMap[kit.status] || statusBadgeMap.draft)} data-testid={`kit-status-${kit.id}`}>
                      {(kit.status || 'draft').toUpperCase()}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 px-4 pb-4 pt-0">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                    <p className="uppercase tracking-[0.12em] text-slate-500">Componentes</p>
                    <p className="mt-1 font-semibold text-slate-900" data-testid={`kit-component-summary-${kit.id}`}>{kit.component_count} itens</p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                    <p className="uppercase tracking-[0.12em] text-slate-500">Disponível</p>
                    <p className="mt-1 font-semibold text-slate-900">{kit.available_quantity} un.</p>
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5" data-testid={`kit-composition-summary-${kit.id}`}>
                  <div className="flex items-center justify-between gap-2 text-xs uppercase tracking-[0.12em] text-slate-500">
                    <span>Composição</span>
                    <span data-testid={`kit-category-${kit.id}`}>{kit.category || 'Sem categoria'}</span>
                  </div>
                  <p className="mt-1 line-clamp-2 text-xs text-slate-700">{getKitCompositionSummary(kit)}</p>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Preço Final</span>
                  <span className="font-semibold text-green-600">{formatCurrency(kit.final_price)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Margem</span>
                  <span className={cn(
                    "font-medium",
                    kit.margin_percent >= 20 ? "text-green-600" : kit.margin_percent >= 10 ? "text-amber-600" : "text-red-600"
                  )}>
                    {kit.margin_percent?.toFixed(1)}%
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Aprovado por</span>
                  <span className="font-medium" data-testid={`kit-approved-by-${kit.id}`}>{kit.approved_by_name || '—'}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Aprovado em</span>
                  <span className="font-medium" data-testid={`kit-approved-at-${kit.id}`}>{kit.approved_at ? new Date(kit.approved_at).toLocaleString('pt-PT', { timeZone: 'Africa/Maputo' }) : '—'}</span>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button size="sm" variant="outline" className="h-9 flex-1 rounded-xl px-3 text-sm" onClick={() => handleViewDetail(kit)} data-testid={`view-kit-${kit.id}`}>
                    <Eye className="h-3 w-3 mr-1" /> Ver
                  </Button>
                  {kit.status === 'draft' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-9 w-9 rounded-xl px-0 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                      onClick={() => handleApproveKit(kit)}
                      disabled={approvingKitId === kit.id}
                      data-testid={`approve-kit-${kit.id}`}
                    >
                      <Check className="h-3 w-3" />
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="h-9 w-9 rounded-xl px-0" onClick={() => handleEdit(kit)} data-testid={`edit-kit-${kit.id}`}>
                    <Edit className="h-3 w-3" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="h-9 w-9 rounded-xl px-0 text-red-600 hover:bg-red-50" 
                    onClick={() => handleDelete(kit)}
                    data-testid={`delete-kit-${kit.id}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="kit-form-dialog">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Editar Kit' : 'Novo Kit'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <Label>Nome do Kit *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Ex: PC Completo Gaming"
                  required
                  data-testid="kit-form-name-input"
                />
              </div>
              <div>
                <Label>Código Comercial (opcional)</Label>
                <Input
                  value={formData.sku}
                  onChange={(e) => setFormData({...formData, sku: e.target.value})}
                  placeholder="Ex: KIT-PC-GAMING-001 (se vazio usa kit_code)"
                  disabled={editMode}
                  data-testid="kit-form-sku-input"
                />
                {!editMode && (
                  <p className="text-[11px] text-muted-foreground mt-1" data-testid="kit-code-autogen-hint">
                    O kit_code será gerado automaticamente (ex: KIT-26-00001)
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <Label>Categoria</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger data-testid="kit-form-category-select">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Use new productCategories from catalog */}
                    {productCategories.map(cat => (
                      <SelectItem key={cat.id} value={cat.base_prefix}>{cat.name}</SelectItem>
                    ))}
                    {/* Fallback to legacy categories if productCategories is empty */}
                    {productCategories.length === 0 && categories.map(cat => (
                      <SelectItem key={cat.key} value={cat.key}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Descrição</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Descrição opcional..."
                  data-testid="kit-form-description-input"
                />
              </div>
            </div>

            {/* Components */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label className="text-base font-semibold">Componentes</Label>
                <Button type="button" variant="outline" size="sm" onClick={handleAddComponent} data-testid="kit-form-add-component-button">
                  <Plus className="h-3 w-3 mr-1" /> Adicionar
                </Button>
              </div>

              {formData.components.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Adicione produtos ao kit
                </p>
              ) : (
                <div className="space-y-2">
                  {formData.components.map((comp, idx) => (
                    <div key={idx} className="grid gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2 md:grid-cols-[minmax(0,1fr)_76px_132px_40px] md:items-center">
                      <div className="min-w-0">
                        <ProductCombobox
                          products={products}
                          value={comp.product_id}
                          onValueChange={(v) => handleComponentChange(idx, 'product_id', v)}
                          placeholder="Pesquisar produto..."
                          testId={`kit-component-product-${idx}`}
                        />
                      </div>
                      <div>
                        <Input
                          type="number"
                          min="1"
                          value={comp.quantity}
                          onChange={(e) => handleComponentChange(idx, 'quantity', e.target.value)}
                          placeholder="Qtd"
                          data-testid={`kit-component-quantity-${idx}`}
                        />
                      </div>
                      <div>
                        <Select 
                          value={comp.component_type} 
                          onValueChange={(v) => handleComponentChange(idx, 'component_type', v)}
                        >
                          <SelectTrigger data-testid={`kit-component-type-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {componentTypes.map(t => (
                              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button type="button" variant="ghost" size="sm" className="h-9 w-9 px-0" onClick={() => handleRemoveComponent(idx)} data-testid={`kit-component-remove-${idx}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pricing */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Preço</Label>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                  <Label>Método de Preço</Label>
                  <Select 
                    value={formData.pricing_method} 
                    onValueChange={(v) => setFormData({...formData, pricing_method: v})}
                  >
                    <SelectTrigger data-testid="kit-form-pricing-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {pricingMethods.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {formData.pricing_method === 'fixed' && (
                  <div>
                    <Label>Preço Fixo</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.fixed_price}
                      onChange={(e) => setFormData({...formData, fixed_price: e.target.value})}
                      placeholder="0.00"
                      data-testid="kit-form-fixed-price-input"
                    />
                  </div>
                )}
                {formData.pricing_method === 'discount' && (
                  <div>
                    <Label>Desconto (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={formData.discount_percent}
                      onChange={(e) => setFormData({...formData, discount_percent: e.target.value})}
                      placeholder="0"
                      data-testid="kit-form-discount-input"
                    />
                  </div>
                )}
              </div>

              {/* Totals Preview */}
              {formData.components.length > 0 && (
                <div className="mt-3 rounded-lg border border-slate-200 bg-slate-100 p-3">
                  <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
                    <div>
                      <p className="text-muted-foreground">Custo Total</p>
                      <p className="font-semibold">{formatCurrency(totals.totalCost)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Soma Preços</p>
                      <p className="font-semibold">{formatCurrency(totals.totalPrice)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Preço Final</p>
                      <p className="font-bold text-green-600">{formatCurrency(totals.finalPrice)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Margem</p>
                      <p className={cn(
                        "font-bold",
                        totals.marginPercent >= 20 ? "text-green-600" : "text-amber-600"
                      )}>
                        {totals.marginPercent.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="kit-form-cancel-button">
                Cancelar
              </Button>
              <Button type="submit" data-testid="kit-form-submit-button">
                {editMode ? 'Actualizar' : 'Criar Kit'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="kit-detail-dialog">
          <DialogHeader>
            <DialogTitle>Detalhes do Kit</DialogTitle>
          </DialogHeader>

          {selectedKit && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold">{selectedKit.name}</h3>
                  <p className="text-sm text-muted-foreground" data-testid="kit-detail-code">{selectedKit.kit_code || selectedKit.sku}</p>
                  <p className="text-xs text-muted-foreground" data-testid="kit-detail-status">Estado: {(selectedKit.status || 'draft').toUpperCase()}</p>
                  <p className="text-xs text-muted-foreground" data-testid="kit-detail-approved-meta">
                    Aprovado por: {selectedKit.approved_by_name || '—'} • {selectedKit.approved_at ? new Date(selectedKit.approved_at).toLocaleString('pt-PT', { timeZone: 'Africa/Maputo' }) : '—'}
                  </p>
                </div>
                <Badge className="text-lg px-4 py-1" variant="outline">
                  {formatCurrency(selectedKit.final_price)}
                </Badge>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-sm text-muted-foreground">Custo</p>
                    <p className="text-xl font-bold">{formatCurrency(selectedKit.total_cost)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-sm text-muted-foreground">Margem</p>
                    <p className="text-xl font-bold text-green-600">{selectedKit.margin_percent?.toFixed(1)}%</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-sm text-muted-foreground">Disponível</p>
                    <p className="text-xl font-bold">{selectedKit.availability?.max_kits_available || 0} un.</p>
                  </CardContent>
                </Card>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Componentes</h4>
                <div className="space-y-2">
                  {selectedKit.availability?.components?.map((comp, idx) => (
                    <div key={idx} className="flex justify-between items-center p-3 bg-slate-50 rounded">
                      <div>
                        <p className="font-medium">{comp.product_name}</p>
                        <p className="text-xs text-muted-foreground">{comp.product_sku}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm">Necessário: {comp.required}</p>
                          <p className="text-sm text-muted-foreground">Disponível: {comp.available}</p>
                        </div>
                        <Badge variant={comp.status === 'ok' ? 'default' : comp.status === 'low' ? 'secondary' : 'destructive'}>
                          {comp.status === 'ok' ? 'OK' : comp.status === 'low' ? 'Baixo' : 'Esgotado'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="kit-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Confirmar Eliminação
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Tem a certeza que deseja eliminar o kit:
            </p>
            <p className="font-semibold mt-2 text-lg">{kitToDelete?.name}</p>
            <p className="text-sm text-muted-foreground">{kitToDelete?.sku}</p>
            <p className="text-xs text-amber-600 mt-4">
              Esta acção não pode ser revertida.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} data-testid="kit-delete-cancel-button">
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDelete} data-testid="kit-delete-confirm-button">
              <Trash2 className="h-4 w-4 mr-2" />
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
